import React, { useEffect } from 'react'
import { Field, change } from 'redux-form'
import { getInsuranceFromRPAServices } from '../../../../containers/authorised/addOns/other/AddOnsHelperFunctions';
import Alert from '../../../../itrust_common/components/Alert';
import { connect } from 'react-redux';
import { claimFormReset } from '../../../../redux/actions/order/claimFormActions';
import { tabChange } from '../../../../itrust_common/redux/actions/otherActions';
import MultiSelectField from '../../../../itrust_common/components/form/MultiSelectField';
import MultiSelectWithSelfPayField from '../../../../itrust_common/components/form/MultiSelectWithSelfPayField';
import { visionInsuranceServicesLoad } from '../../../../redux/actions/addOns/rpa/vision_insurance/visionInsuranceAction';
import { visionInsuranceClaimFormReset } from '../../../../redux/actions/addOns/rpa/vision_insurance/visionInsuranceClaimFormAction';
import { ediClearingHouseServicesLoad } from '../../../../redux/actions/addOns/EDIClearingHouse/EdiClearningHouseActions';

function OrderInsurancePayer(props) {
  const { claimFormData, order, dispatch, activeTab, visionInsuranceConfig, services } = props

  useEffect(() => {
    let storeID = localStorage.getItem('StoreID')
    if (storeID == null) {
      // Store dropdown All selected > load RPA services from order's store_id
      dispatch(visionInsuranceServicesLoad({ store_id: order.store_id, category: "vision_insurance" }))
      dispatch(ediClearingHouseServicesLoad({ store_id: order.store_id, category: "edi_clearing_house" }))
    }
  }, [])

  const getSelectedInsuranceIds = () => {
    const ids = (order.orders_insurances_attributes || []).map((orderInsurance) => orderInsurance.patient_insurance_id);
    if (order.self_pay) {
      ids.push('self_pay');
    }
    return ids;
  }

  const buildOrdersInsurancesAttributes = (selectedIds) => {
    const existing = order.orders_insurances_attributes || [];
    const attrs = [];

    existing.forEach((orderInsurance) => {
      if (!selectedIds.includes(orderInsurance.patient_insurance_id)) {
        attrs.push({ id: orderInsurance.id, patient_insurance_id: orderInsurance.patient_insurance_id, _destroy: true });
      }
    });

    selectedIds.forEach((patientInsuranceId) => {
      const existingOrderInsurance = existing.find((orderInsurance) => orderInsurance.patient_insurance_id === patientInsuranceId);
      if (existingOrderInsurance) {
        attrs.push({ id: existingOrderInsurance.id, patient_insurance_id: patientInsuranceId });
      } else {
        attrs.push({ patient_insurance_id: patientInsuranceId });
      }
    });

    return attrs;
  }

  const applyInsuranceSelection = (selectedIds) => {
    const isSelfPay = selectedIds.includes('self_pay');
    const filteredIds = selectedIds.filter(id => id !== 'self_pay');
    
    dispatch(change('orderHeaderForm', 'self_pay', isSelfPay));
    dispatch(change('orderHeaderForm', 'patient_insurance_ids', selectedIds));
    dispatch(change('orderHeaderForm', 'orders_insurances_attributes', buildOrdersInsurancesAttributes(filteredIds)));
  }

  const getInsuranceChangeAlert = (selectedIds) => {
    const previousIds = getSelectedInsuranceIds();
    const addedIds = selectedIds.filter((id) => !previousIds.includes(id));
    const removedIds = previousIds.filter((id) => !selectedIds.includes(id));
    const changedIds = [...addedIds, ...removedIds];

    if (!changedIds.length) return null;

    const insuranceOptions = insuranceOptionsList();
    const getInsuranceTypeCategory = (patientInsuranceId) => {
      const option = insuranceOptions.find((item) => item.id === patientInsuranceId);
      return option?.insuranceType?.slice(0, 13) === 'PatientVision' ? 'vision' : 'medical';
    }

    const existingTypes = previousIds.map(getInsuranceTypeCategory);
    const selectedTypes = selectedIds.map(getInsuranceTypeCategory);
    const hasVisionToMedicalChange = existingTypes.some((type) => type === 'vision') && selectedTypes.some((type) => type === 'medical')
      || existingTypes.some((type) => type === 'medical') && selectedTypes.some((type) => type === 'vision');

    if (hasVisionToMedicalChange) {
      return 'You are changing insurance from Vision to Medical.';
    }

    if (visionInsuranceConfig?.status && visionInsuranceConfig?.status !== 'inactive') {
      const changedInsuranceNames = changedIds.map((id) => insuranceOptions.find((item) => item.id === id)?.insuranceName).filter(Boolean);
      const hasRpaInsuranceChange = changedInsuranceNames.some((name) => getInsuranceFromRPAServices(services, name));
      if (hasRpaInsuranceChange) {
        return removedIds.length && !addedIds.length
          ? 'You are removing an RPA Vision Insurance.'
          : 'You are changing Vision insurance from or to an RPA Insurance.';
      }
    }

    if (removedIds.length && !addedIds.length) {
      return 'You are removing the selected insurance payer(s).';
    }

    return changedIds.length ? 'You are changing the selected insurance payer(s).' : null;
  }

  const handleAllowClaimDelete = () => {
    dispatch(change('orderHeaderForm', 'is_destroy_claim_form', true));
    dispatch(claimFormReset());
    dispatch(visionInsuranceClaimFormReset());
    activeTab === 6 && dispatch(tabChange({ order: 1 }));
  }

  const handleOnInsuranceChange = (selectedIds) => {
    const previousIds = getSelectedInsuranceIds();
    const normalizedSelectedIds = selectedIds || [];

    if (JSON.stringify([...previousIds].sort()) === JSON.stringify([...normalizedSelectedIds].sort())) {
      return;
    }

    const removedIds = previousIds.filter((id) => !normalizedSelectedIds.includes(id));
    const isUnchecking = removedIds.length > 0;
    const alertText = getInsuranceChangeAlert(normalizedSelectedIds);

    const hasRemovedClaimForm = (order.orders_insurances_attributes || []).some(
      (item) => removedIds.includes(item.patient_insurance_id) && (item.claim_form_id || item.visionary_insurance_claim_form_id)
    );

    if (isUnchecking && alertText && hasRemovedClaimForm) {
      Alert.confirm(`Claim form for this order exists. ${alertText} Clicking 'Continue' will delete the existing claim form.`, (res) => {
        if (res) {
          handleAllowClaimDelete();
          applyInsuranceSelection(normalizedSelectedIds);
        } else {
          dispatch(change('orderHeaderForm', 'patient_insurance_ids', previousIds));
        }
      });
      return;
    }

    applyInsuranceSelection(normalizedSelectedIds);
  }

  const insuranceOptionsList = () => {
    const patientAttributes = order.patient_attributes;
    let options = [];

    options.push({
      insuranceType: 'self_pay',
      insuranceName: 'Self Pay',
      id: 'self_pay',
    });

    const insuranceTypes = ['patient_vision_insurance', 'patient_medical_insurance', 'patient_secondary_insurance', 'patient_tertiary_insurance', 'patient_vision_secondary_insurance', 'patient_vision_tertiary_insurance'];
    insuranceTypes.forEach((insuranceType) => {
      if (patientAttributes?.[insuranceType]) {
        options = [...options, {
          insuranceType: patientAttributes[insuranceType].insurance_type,
          insuranceName: patientAttributes[insuranceType]?.insurance_name,
          id: patientAttributes[insuranceType]?.id,
        }];
      }
    });
    return options;
  }

  return (
    <>
      <Field
        name="patient_insurance_ids"
        label="Insurance Payer"
        component={MultiSelectWithSelfPayField}
        data={insuranceOptionsList()}
        onSelectHook={handleOnInsuranceChange}
        textField="insuranceName"
        valueField="id"
        isAllSelectVisible={false}
        isSearchVisible={true}
        placeholder="Select insurance payer"
        key={order.id}
        wrapperClasses="form-group col-3 pr-0"
      />
    </>
  )
}


const mapStateToProps = (state) => {
  const { organisation: { organisation }, visionInsurance: { services }, order: { order } } = state

  return {
    visionInsuranceConfig:organisation.add_ons_config?.type?.vision_insurance,
    services,
    order,
  }
}

export default connect(mapStateToProps)(OrderInsurancePayer)